import { ActivityCenterTabsEnum } from '@/components/ActivityCenter/components/ActivitiesCenterTabs';
import {
  CATEGORY_ID_QUERY_PARAM,
  CATEGORY_WIDGET_ID_QUERY_PARAM,
} from '@/modules/corporate/modules/widget/views/WidgetDetails/hooks/useExpandedCategoryQueryParams';
import { TeamDashboardTabsEnum } from '@/modules/learner/views/TeamDashboard/components/TeamDashboardContent/utils';
import { PraisesTabEnum } from '@/modules/praises/views/PraiseWall/types';
import { EmployeeProfilePages } from '@/modules/user/views/UserProfile/types';
import { MgsaTabsEnum } from '@/modules/user/views/UserProfile/views/EmployeeProfileMgsa/types';
import { ActionTypes } from '@/modules/user/views/UserProfile/views/UserProfileActionDetails/types';

import oneOnOneRoutes from './1on1s';
import actionPlanRoutes from './action-plan';
import adminRoutes from './admin';
import alignAndEngageRoutes from './align-and-engage';
import announcementRoutes from './announcements';
import assignmentRoutes from './assignment';
import contentRoutes from './contents';
import departmentRoutes from './department';
import designations from './designations';
import devRoutes from './dev';
import eventRoutes from './event';
import feedbackRoutes from './feedback';
import gamificationAdminRoutes from './gamification';
import groupRoutes from './group';
import habitsRoutes from './habits';
import integrationsRoutes from './integrations';
import {
  adminRoutes as journeyAdminRoutes,
  learnerRoutes as journeyLearnerRoutes,
} from './journey';
import knowledgeAssetRoutes, { knowledgeAssetLearnerRoutes } from './knowledge-asset';
import learnerRoutes from './learner';
import { learnerRoutes as librayPortalLearnerRoutes } from './libray-portlal';
import lyearnConnectRoutes from './lyearn-connect';
import meetingRoutes from './meetings';
import metricRoutes from './metrics';
import orgRoles from './org-roles';
import productivityRoutes from './productivity';
import reviewRoutes from './review';
import scorecardRoutes from './scorecard';
import skillRoutes from './skill-matrix';
import staffRoleRoutes from './staff-roles';
import tagRoutes from './tags';
import taskRoutes from './tasks';
import trackRoutes from './track';
import userRoutes from './user';
import userSurveyRoutes from './user-survey';
import workeffyRoutes from './workeffy';

const routes = {
  Database: (databaseId: string) => `/databases/${databaseId}`,

  WidgetDetails: (widgetId: string) => `/widget/${widgetId}`,
  ExpandedCategoryWidgetDetails: (
    expandedCategoryWidgetId: string,
    categoryId: string,
    categoryWidgetId: string,
  ) =>
    `/widget/${expandedCategoryWidgetId}?${CATEGORY_ID_QUERY_PARAM}=${categoryId}&${CATEGORY_WIDGET_ID_QUERY_PARAM}=${categoryWidgetId}`,
  QuizPlayer: (contentId: string, action: string) => `/quiz/player/${contentId}/${action}`,

  CommunityDetails: (communityId: string) => `/community/${communityId}`,
  CommunityChannelDetails: (communityId: string, channelId: string) =>
    `/community/${communityId}/spaces/${channelId}`,

  // skills-review
  SkillReview: (reviewCycleId: string) => `/review-cycle/${reviewCycleId}/review`,
  SkillUserResults: (reviewCycleId: string) => `/review-cycle/${reviewCycleId}/results`,
  SkillUserShareResults: (reviewCycleId: string) => `/review-cycle/${reviewCycleId}/share-results`,
  SkillUserShareResultsUser: (reviewCycleId: string, userId: string) =>
    `/review-cycle/${reviewCycleId}/share-results/user/${userId}`,
  SkillUserManageReviews: (reviewCycleId: string) =>
    `/review-cycle/${reviewCycleId}/manage-reviews`,
  SkillUserReview: (reviewCycleId: string, userId: string) =>
    `/review-cycle/${reviewCycleId}/review/user/${userId}`,
  SkillPeerSelection: (reviewCycleId: string) => `/review-cycle/${reviewCycleId}/peers`,
  SkillPeerNominationRequests: (reviewCycleId: string) =>
    `/review-cycle/${reviewCycleId}/peer-requests`,
  SkillPeerSelectionForUser: (reviewCycleId: string, userId: string) =>
    `/review-cycle/${reviewCycleId}/peers/user/${userId}`,
  SkillSubmissionPreview: (
    reviewCycleId: string,
    userId: string,
    reviewerId: string,
    reviewType: string,
  ) =>
    `/review-cycle/${reviewCycleId}/preview/user/${userId}/reviewer/${reviewerId}/review/${reviewType}`,

  // recorder
  Recorder: () => '/record',

  //praises
  Praises: (tabId?: PraisesTabEnum) => `/praises/${tabId ?? ''}`,

  //All Sessions
  AllSessions: () => `/session/all`,

  // User Profile
  UserProfile: (id: string, type?: EmployeeProfilePages) => `/user-profile/${id}/${type ?? ''}`,
  UserMgsaProfile: (id: string) => `/w/profile/${id}`,
  UserProfileMgsa: (id: string, mgsaTab: MgsaTabsEnum) =>
    `/user-profile/${id}/${EmployeeProfilePages.Mgsa}?mgsa-tab=${mgsaTab}`,
  UserActivityDetails: (userId: string, metricId: string) =>
    `/user-profile/${userId}/${EmployeeProfilePages.Activities}/${metricId}`,
  LearnerActivityDetails: (userId: string, metricId: string) =>
    `/learners/${userId}/${EmployeeProfilePages.Activities}/${metricId}`,
  UserProfileSkillDetailPage: (userId: string, skillId: string) =>
    `/user-profile/${userId}/${EmployeeProfilePages.Skills}/${skillId}`,
  UserProfileSkillsRecommendedLearning: (userId: string, skillId: string) =>
    `/user-profile/${userId}/${EmployeeProfilePages.Skills}/${skillId}/learnings`,
  UserProfileSkillsAllActions: (userId: string, actionType: ActionTypes, skillId?: string) =>
    skillId
      ? `/user-profile/${userId}/${EmployeeProfilePages.Skills}/${skillId}/actions/${actionType}`
      : `/user-profile/${userId}/${EmployeeProfilePages.Skills}/actions/${actionType}`,
  UserProfileMindsetDetailPage: (userId: string, mindsetId: string) =>
    `/user-profile/${userId}/${EmployeeProfilePages.Mindsets}/${mindsetId}`,

  // Team
  TeamProfile: (id: string, type?: TeamDashboardTabsEnum) => `/team-profile/${id}/${type ?? ''}`,
  TeamProfileMgsa: (id: string, mgsaTab: MgsaTabsEnum) =>
    `/team-profile/${id}/${TeamDashboardTabsEnum.Mgsa}?mgsa-tab=${mgsaTab}`,

  //not-supported
  NotSupported: () => '/not-supported',

  //templates
  OKRsTemplates: () => '/templates/okrs',
  ReviewCycleTemplates: () => '/admin/templates/review-cycle',

  //academy-setup
  AcademySetup: () => '/academy-setup',

  //solution-center
  SolutionCenter: () => '/solution-center',
  ActivityCenter: () => `/activity-center`,

  ActivityCenterTab: (tab?: ActivityCenterTabsEnum) => `/activity-center?tab=${tab}`,

  ...devRoutes,
  ...adminRoutes,
  ...learnerRoutes,
  ...departmentRoutes,
  ...trackRoutes,
  ...userRoutes,
  ...contentRoutes,
  ...eventRoutes,
  ...assignmentRoutes,
  ...scorecardRoutes,
  ...tagRoutes,
  ...groupRoutes,
  ...staffRoleRoutes,
  ...lyearnConnectRoutes,
  ...skillRoutes,
  ...orgRoles,
  ...designations,
  ...integrationsRoutes,
  ...productivityRoutes,
  ...reviewRoutes,
  ...oneOnOneRoutes,
  ...meetingRoutes,
  ...feedbackRoutes,
  ...taskRoutes,
  ...metricRoutes,
  ...userSurveyRoutes,
  ...actionPlanRoutes,
  ...habitsRoutes,
  ...journeyAdminRoutes,
  ...journeyLearnerRoutes,
  ...announcementRoutes,
  ...gamificationAdminRoutes,
  ...knowledgeAssetRoutes,
  ...librayPortalLearnerRoutes,
  ...knowledgeAssetLearnerRoutes,
  ...alignAndEngageRoutes,
  ...workeffyRoutes,
};

export default routes;
